import React, { useEffect, useState } from 'react';
import './ChecklistModal.css';
import { auth } from '../../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { formatInstantForViewer, getViewerTimezone } from '../../utils/dateTime';

/**
 * ChecklistModal
 *
 * Popup for the inspection-report Checklist feature. Same component renders
 * both entry points: "Generate Checklist" (mode="generate" — POSTs to
 * generate, holds a loading state for the duration of the parse) and
 * "View Generated" (mode="view" — GETs the already-generated checklist).
 * Both calls return the same ChecklistResponse shape, so the render logic
 * below doesn't care which one populated it.
 *
 * Priority is tap-to-edit inline (click the pill, pick a value) — no
 * separate edit screen, matches the feature spec. "Raise Service Request"
 * hands the item up to the caller, which owns navigating to the
 * Create-Service-Request flow with prefilled fields (this modal doesn't
 * know about that flow's internals).
 */

const PRIORITY_LABELS = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' };
const PRIORITY_ORDER = ['HIGH', 'MEDIUM', 'LOW'];

const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(false).then(resolve).catch(reject);
  } else {
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (user) user.getIdToken(false).then(resolve).catch(reject);
      else reject(new Error('Not authenticated'));
    });
    setTimeout(() => reject(new Error('Auth timeout')), 10000);
  }
});

const PriorityPill = ({ itemId, priority, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="checklist-priority-wrap">
      <button
        type="button"
        className={`checklist-priority-pill checklist-priority-${priority?.toLowerCase()}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
      >
        {PRIORITY_LABELS[priority] || priority}
      </button>
      {open && (
        <>
          <div className="checklist-priority-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="checklist-priority-menu">
            {PRIORITY_ORDER.map((p) => (
              <button
                key={p}
                type="button"
                className={`checklist-priority-menu-item checklist-priority-${p.toLowerCase()}`}
                onClick={() => { onChange(itemId, p); setOpen(false); }}
              >
                {PRIORITY_LABELS[p]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ChecklistModal = ({
  serviceRequestId,
  attachmentId,
  mode, // 'generate' | 'view'
  reportFileUrl,
  reportFileName,
  onClose,
  onRaiseServiceRequest,
}) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingItemId, setSavingItemId] = useState(null);
  const viewerTimezone = getViewerTimezone();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const token = await getFirebaseToken();
        const url = mode === 'generate'
          ? API_ENDPOINTS.generateChecklist(serviceRequestId, attachmentId)
          : API_ENDPOINTS.getChecklist(serviceRequestId, attachmentId);
        const res = mode === 'generate'
          ? await apiClient.post(url, {}, { headers: { Authorization: `Bearer ${token}` } })
          : await apiClient.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setChecklist(res.data?.data || res.data || null);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err?.response?.data?.message
            || 'Could not load the checklist. Please try again.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceRequestId, attachmentId, mode]);

  const handlePriorityChange = async (itemId, priority) => {
    if (!checklist) return;
    const prevItems = checklist.items;
    // Optimistic update
    setChecklist({
      ...checklist,
      items: checklist.items.map((it) => (it.id === itemId ? { ...it, priority } : it)),
    });
    setSavingItemId(itemId);
    try {
      const token = await getFirebaseToken();
      await apiClient.patch(
        API_ENDPOINTS.updateChecklistItemPriority(serviceRequestId, attachmentId, itemId),
        { priority },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Roll back on failure
      setChecklist((c) => (c ? { ...c, items: prevItems } : c));
      window.alert('Could not update priority. Please try again.');
    } finally {
      setSavingItemId(null);
    }
  };

  return (
    <div className="checklist-modal-overlay" onClick={onClose}>
      <div
        className="checklist-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Report Checklist"
      >
        <div className="checklist-modal-header">
          <div className="checklist-modal-header-text">
            <h2 className="checklist-modal-title">Checklist</h2>
            {checklist?.generatedAt && (
              <p className="checklist-modal-meta">
                Generated {formatInstantForViewer(checklist.generatedAt, viewerTimezone).dateLabel} · Visible only to you
              </p>
            )}
          </div>
          <button
            className="checklist-modal-close"
            onClick={onClose}
            aria-label="Close checklist"
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="checklist-modal-body">
          {loading && (
            <div className="checklist-modal-loading">
              <span className="checklist-modal-spinner" aria-hidden="true" />
              {mode === 'generate' ? 'Parsing the report and generating your checklist…' : 'Loading checklist…'}
            </div>
          )}

          {!loading && loadError && (
            <div className="checklist-modal-error">{loadError}</div>
          )}

          {!loading && !loadError && checklist?.status === 'FAILED' && (
            <div className="checklist-modal-error">
              {checklist.failureReason || 'Could not generate a checklist from this report.'}
            </div>
          )}

          {!loading && !loadError && checklist?.status === 'READY' && (
            <>
              <div className="checklist-modal-disclaimer">
                ⚠️ {checklist.disclaimerText
                  || "Summary generated for convenience — refer to the full report for complete details."}
              </div>

              {(checklist.items || []).length === 0 && (
                <div className="checklist-modal-empty">No findings were extracted from this report.</div>
              )}

              {(checklist.items || []).map((item) => (
                <div key={item.id} className="checklist-item-row">
                  <div className="checklist-item-top">
                    <div className="checklist-item-text">
                      <div className="checklist-item-area">{item.areaOrItemName}</div>
                      <div className="checklist-item-summary">{item.summary}</div>
                    </div>
                    <PriorityPill
                      itemId={item.id}
                      priority={item.priority}
                      onChange={handlePriorityChange}
                      disabled={savingItemId === item.id}
                    />
                  </div>
                  <div className="checklist-item-bottom">
                    <div className="checklist-item-photos">
                      {item.photoRefs && item.photoRefs.length > 0
                        ? `📷 ${item.photoRefs.length} photo${item.photoRefs.length > 1 ? 's' : ''} referenced`
                        : 'No photo referenced'}
                    </div>
                    {item.raisedServiceRequestId ? (
                      <span className="checklist-item-raised-pill">✓ Service Request Raised</span>
                    ) : (
                      <button
                        type="button"
                        className="checklist-raise-sr-btn"
                        onClick={() => onRaiseServiceRequest && onRaiseServiceRequest(item, checklist)}
                      >
                        Raise Service Request
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {reportFileUrl && (
                <button
                  type="button"
                  className="checklist-original-link"
                  onClick={() => window.open(reportFileUrl, '_blank', 'noopener,noreferrer')}
                >
                  📄 View full original report{reportFileName ? ` (${reportFileName})` : ''}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistModal;
